import React, { useRef, useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import Avatar from "@mui/material/Avatar";

const FilterSelect = ({
  options,
  selected,
  onChange,
  placeholder,
  isDisabled,
  isMulti,
  showMultiValueCountSummary = true,
  bgColor = "var(--app-surface-2)",
  width = "150px",
  showAvatar = false,
  showSearchBar = false, //  Default is false now
}) => {
  const selectRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const instanceIdRef = useRef(`filterselect-${Math.random().toString(36).slice(2)}`);

  // Ensure only one FilterSelect menu remains open at a time
  useEffect(() => {
    const handleAnyOpen = (e) => {
      const openedId = e?.detail?.id;
      if (openedId && openedId !== instanceIdRef.current) {
        setMenuIsOpen(false);
      }
    };
    window.addEventListener("filterselect:open", handleAnyOpen);
    return () => {
      window.removeEventListener("filterselect:open", handleAnyOpen);
    };
  }, []);

  //  Filter options only if search bar is enabled
  const filteredOptions = useMemo(() => {
    if (!showSearchBar || !searchTerm.trim()) return options;
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, options, showSearchBar]);

  //  Styles
  const customStyles = {
    control: (provided) => ({
      ...provided,
      width,
      minHeight: "40px",
      border: "0",
      borderRadius: "8px",
      boxShadow: "none",
      backgroundColor: bgColor,
      padding: "0 8px",
      cursor: "pointer",
    }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: "none" }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: "transparent",
      caretColor: "transparent",
      pointerEvents: "none",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0",
      flexWrap: "nowrap",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--app-text)",
      backgroundColor: "transparent",
      padding: 0,
      whiteSpace: "nowrap",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "15px",
      color: "var(--app-text)",
      whiteSpace: "nowrap",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999999,
      borderRadius: "8px",
      width: "250px",
      overflow: "hidden",
      backgroundColor: "var(--app-surface)",
      border: "1px solid var(--app-border)",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "15px",
      color: "var(--app-text)",
      backgroundColor:
        state.isSelected || state.isFocused
          ? "var(--app-surface-2)"
          : "var(--app-surface)",
      cursor: "default",
      padding: "10px 12px",
      borderBottom: "1px solid var(--app-border)",
    }),
  };

  //  Label with optional avatar
  const formatOptionLabel2 = (option, { context }) => {
    if (context === "menu") {
      return (
        <div className="symptom-option d-flex align-items-center gap-2">
          {showAvatar &&
            (option.profileImage ? (
              <Avatar src={option.profileImage} alt={option.label} sx={{ width: 30, height: 30 }} />
            ) : (
              <Avatar sx={{ width: 30, height: 30, fontSize: 13 }}>
                {option.label?.split(" ").map((n) => n[0]).slice(0, 2).join("")}
              </Avatar>
            ))}
          <div className="symptom-title">{option.label}</div>
        </div>
      );
    }
    return (
      <span style={{ color: "var(--app-text)", display: "flex", alignItems: "center" }}>
        {showAvatar &&
          (option.profileImage ? (
            <Avatar src={option.profileImage} alt={option.label} sx={{ width: 24, height: 24, mr: "6px" }} />
          ) : (
            <Avatar sx={{ width: 24, height: 24, mr: "6px", fontSize: 11 }}>
              {option.label?.[0] || "?"}
            </Avatar>
          ))}
        {option.label}
      </span>
    );
  };

  //  Checkbox-based Option
  const CustomOption = (props) => {
    const { data, isSelected, selectProps } = props;

    const handleCheckboxChange = (e) => {
      e.stopPropagation();
      let newValues = Array.isArray(selectProps.value) ? [...selectProps.value] : [];
      if (e.target.checked) {
        newValues.push(data);
      } else {
        newValues = newValues.filter((v) => v.value !== data.value);
      }
      selectProps.onChange(newValues);
      selectProps.setSearchTerm("");
    };

    return (
      <components.Option {...props} innerProps={{ ...props.innerProps, onClick: undefined }}>
        <div className="d-flex align-items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            onClick={(e) => e.stopPropagation()}
          />
          {formatOptionLabel2(data, { context: "menu" })}
        </div>
      </components.Option>
    );
  };

  //  Small reusable SearchBar component
  const SearchBar = ({ value, onChange }) => (
    <div
      style={{
        padding: "8px 10px",
        borderBottom: "1px solid var(--app-border)",
        backgroundColor: "var(--app-surface-2)",
        display: "flex",
        alignItems: "center",
      }}
      role="presentation"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div style={{ position: "relative", width: "100%" }}>
        <i
          className="iconly-Search icli sidebar_icons"
          style={{
            position: "absolute",
            left: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--app-text-muted)",
            pointerEvents: "none",
            fontSize: "18px",
          }}
        />
        <input
          type="text"
          className="form-control searchbox"
          placeholder="Search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: "100%",
            padding: "6px 8px 6px 35px",
            borderRadius: "6px",
            border: "1px solid var(--app-border)",
            outline: "none",
            backgroundColor: "var(--app-surface)",
          }}
        />
      </div>
    </div>
  );

  //  Custom MenuList
  const CustomMenuList = (props) => {
    const { children } = props;
    return (
      <div>
        {showSearchBar && <SearchBar value={searchTerm} onChange={setSearchTerm} />}
        {React.Children.count(children) > 0 ? (
          <components.MenuList {...props}>{children}</components.MenuList>
        ) : (
          <div style={{ padding: "10px", color: "var(--app-text-muted)" }}>No results found</div>
        )}
      </div>
    );
  };

  // Custom Value Summary for multiselect
  const CustomValueContainer = (props) => {
    const { selectProps, getValue } = props;
    const selectedValues = getValue ? getValue() : [];
    const hasSelected = Array.isArray(selectedValues) && selectedValues.length > 0;

    return (
      <components.ValueContainer {...props}>
        {selectProps.isMulti &&
          selectProps.showMultiValueCountSummary &&
          hasSelected && (
            <span style={{ fontSize: "14px", color: "var(--app-text)", marginRight: 6 }}>
              {(selectProps.placeholder || "Selected") +
                ` (${selectedValues.length})`}
            </span>
          )}
        {props.children}
      </components.ValueContainer>
    );
  };

  // PropTypes for internal components (defined inside to avoid scope issues)
  CustomOption.propTypes = {
    data: PropTypes.object.isRequired,
    isSelected: PropTypes.bool,
    selectProps: PropTypes.object.isRequired,
    innerProps: PropTypes.object,
  };

  SearchBar.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
  };

  CustomMenuList.propTypes = {
    children: PropTypes.node,
  };

  CustomValueContainer.propTypes = {
    selectProps: PropTypes.object.isRequired,
    getValue: PropTypes.func,
    children: PropTypes.node,
  };

  return (
    <Select
      ref={selectRef}
      options={filteredOptions}
      isMulti={isMulti}
      styles={customStyles}
      formatOptionLabel={formatOptionLabel2}
      placeholder={placeholder}
      isClearable={false}
      menuPlacement="auto"
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      backspaceRemovesValue={false}
      isSearchable={false}
      menuIsOpen={menuIsOpen}
      components={{
        Option: CustomOption,
        MenuList: CustomMenuList,
        ...(isMulti && showMultiValueCountSummary
          ? { ValueContainer: CustomValueContainer, MultiValue: () => null }
          : {}),
      }}
      onChange={onChange}
      value={selected}
      isDisabled={isDisabled}
      showMultiValueCountSummary={showMultiValueCountSummary}
      setSearchTerm={setSearchTerm}
      onMenuOpen={() => {
        setMenuIsOpen(true);
        window.dispatchEvent(new CustomEvent("filterselect:open", { detail: { id: instanceIdRef.current } }));
      }}
      onMenuClose={() => {
        setMenuIsOpen(false);
        setSearchTerm("");
      }}
    />
  );
};

FilterSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string.isRequired,
      profileImage: PropTypes.string,
    })
  ).isRequired,
  selected: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  showMultiValueCountSummary: PropTypes.bool,
  bgColor: PropTypes.string,
  width: PropTypes.string,
  showAvatar: PropTypes.bool,
  showSearchBar: PropTypes.bool,
};

FilterSelect.defaultProps = {
  selected: undefined,
  placeholder: undefined,
  isDisabled: false,
  isMulti: false,
  showMultiValueCountSummary: true,
  bgColor: "var(--app-surface-2)",
  width: "150px",
  showAvatar: false,
  showSearchBar: false,
};

export default FilterSelect;
